import { ExceptionEvent, Zone } from "@fairfleet/geotab";
import { StayRow } from "../models/StayRow";
import { zoneTypeId } from "@/pages/zones/utils/zoneTypeId";

export interface StayContext {
  /** Rule id → the zone type it records. */
  ruleType: Map<string, string>;
  /** Event id → zones at the vehicle's position when the visit started, in Geotab's order (GetAddresses). */
  visitZones: Map<string, string[]>;
  zones: Map<string, Zone>;
  /** Zone type id → label, for a visit whose zone cannot be found. */
  typeNames: Map<string, string>;
  deviceNames: Map<string, string>;
  typeLabel(zone: Zone): string;
  from: Date;
  to: Date;
  now: Date;
  /** Device id → time of its latest data (DeviceStatusInfo.dateTime). */
  lastData?: Map<string, Date>;
}

const MS_PER_HOUR = 3_600_000;

const idOf = (ref: unknown) => (typeof ref === "string" ? ref : (ref as { id?: string } | undefined)?.id);

/**
 * Like the MyGeotab exception report: the first zone of the rule's type at the start position – among the zones
 * with stop detection on. „excluded“ when the type's zones there all have it off: such a visit is not recorded.
 */
function zoneOfVisit(
  zoneIds: string[] | undefined,
  typeId: string | undefined,
  zones: Map<string, Zone>,
): Zone | "excluded" | undefined {
  if (!typeId) return undefined;
  let excluded = false;
  for (const id of zoneIds ?? []) {
    const zone = zones.get(id);
    if (!zone?.zoneTypes?.some(ref => zoneTypeId(ref) === typeId)) continue;
    if (zone.mustIdentifyStops) return zone;
    excluded = true;
  }
  return excluded ? "excluded" : undefined;
}

/**
 * One row per visit. A visit belongs to the period its entry lies in, with its full real exit and duration.
 * A zone with two types raises both type rules: same vehicle, zone and second of entry is shown once.
 */
export function toStayRows(events: ExceptionEvent[], ctx: StayContext): StayRow[] {
  const byVisit = new Map<string, StayRow>();
  for (const event of events) {
    const entry = new Date(event.activeFrom);
    if (Number.isNaN(entry.getTime()) || entry < ctx.from || entry > ctx.to) continue;
    const ruleId = idOf(event.rule) ?? "";
    const deviceId = idOf(event.device) ?? "";
    const typeId = ctx.ruleType.get(ruleId);
    const found = zoneOfVisit(ctx.visitZones.get(event.id), typeId, ctx.zones);
    if (found === "excluded") continue;
    const zone = found;
    const activeTo = event.activeTo ? new Date(event.activeTo) : null;
    // A visit still running ends, in Geotab, on the vehicle's latest data point (live fairfleet 2026-11-04:
    // activeTo = DeviceStatusInfo.dateTime = last LogRecord, vehicle parked in the zone).
    const lastData = ctx.lastData?.get(deviceId);
    const ongoing =
      !activeTo ||
      Number.isNaN(activeTo.getTime()) ||
      activeTo > ctx.now ||
      (!!lastData && activeTo.getTime() >= lastData.getTime());
    const exit = ongoing ? null : activeTo;
    const row: StayRow = {
      id: event.id,
      deviceId,
      deviceName: ctx.deviceNames.get(deviceId) ?? deviceId,
      zoneId: zone?.id ?? "",
      zoneName: zone?.name ?? "",
      zoneTypes: zone ? ctx.typeLabel(zone) : ((typeId && ctx.typeNames.get(typeId)) ?? ""),
      zoneFound: !!zone,
      entry,
      exit,
      durationMs: Math.max(0, (exit ?? ctx.now).getTime() - entry.getTime()),
      ongoing,
    };
    const key = zone ? `${deviceId}|${zone.id}|${Math.floor(entry.getTime() / 1000)}` : event.id;
    const known = byVisit.get(key);
    if (!known || longer(row, known)) byVisit.set(key, row);
  }
  return [...byVisit.values()].sort(
    (a, b) => a.deviceName.localeCompare(b.deviceName, "de") || a.entry.getTime() - b.entry.getTime(),
  );
}

const longer = (a: StayRow, b: StayRow) => (a.ongoing && !b.ongoing) || (a.ongoing === b.ongoing && a.durationMs > b.durationMs);

/** Null while still inside, so a report's sum stays the same whenever it is exported. */
export function stayDurationHours(row: Pick<StayRow, "ongoing" | "durationMs">): number | null {
  // Unrounded: the grid and Excel show 2 decimals, but a sum over many stays must match the real total.
  return row.ongoing ? null : row.durationMs / MS_PER_HOUR;
}
